import { ColumnSourceEnum, PropertyBrowserTreeNode } from "api-client";
import PropertyInput from "./PropertyInput";
import DropdownBrowser from "components/DropdownBrowser";
import { ConditionProps } from "./ConditionRow";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  PROPERTY_RULE_OPERATORS,
  PROPERTY_TEMPLATE_OPERATORS,
} from "features/MappingTools/DynamicMappingEditor/const";
import React from "react";
import { rulesSetStore } from "../RulesSetStore";
import { observer } from "mobx-react-lite";

interface PropertyFieldProps {
  items?: PropertyBrowserTreeNode[];
  row: ConditionProps;
  onSelectValue: (value: PropertyBrowserTreeNode | string) => void;
  onSelectOperator: (
    value: string | undefined | number,
    update?: boolean
  ) => void;
  onRemoveValue: () => void;
}

function ValuePropertyField(props: PropertyFieldProps) {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const row = props.row;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [valueSearchMode, setValueSearchMode] = React.useState(false);
  const [valueSearchTerm, setValueSearchTerm] = React.useState("");
  const [optionsFocus, setOptionsFocus] = React.useState(false);

  React.useEffect(() => {
    setValueSearchTerm("");
    setOptionsFocus(false);
  }, [rulesSetStore.currentRule?.id]);

  const isTypeNumber = () => row.operator?.includes("THAN");
  const isCurrentSourceTemplate =
    row.value_source === ColumnSourceEnum.TemplateVariable;

  const hideValueInput = () => {
    if (row.operator?.includes("EMPTY")) {
      return true;
    }
    return false;
  };

  const handleChangeValue = (value: string) => {
    if (row.valueProperty) return;
    isCurrentSourceTemplate &&
      props.onSelectOperator(PROPERTY_RULE_OPERATORS[0].id, false);
    props.onSelectValue(value);
    setValueSearchMode(false);
    setOptionsFocus(false);
  };

  const handleSelectPropertyValue = (item: PropertyBrowserTreeNode) => {
    if (item.id === "search_term") {
      handleChangeValue(valueSearchTerm);
      return;
    }
    const switchToTemplateSource =
      item.column_source === ColumnSourceEnum.TemplateVariable &&
      !isCurrentSourceTemplate;
    const switchToOtherSources =
      item.column_source != ColumnSourceEnum.TemplateVariable &&
      isCurrentSourceTemplate;

    if (switchToTemplateSource) {
      props.onSelectOperator(PROPERTY_TEMPLATE_OPERATORS[0].id, false);
    } else if (switchToOtherSources) {
      props.onSelectOperator(PROPERTY_RULE_OPERATORS[0].id, false);
    }
    props.onSelectValue(item);
    setValueSearchTerm("");
    setValueSearchMode(false);
    setOptionsFocus(false);
  };

  const changePropertyFocus = () => {
    if (optionsFocus) {
      setOptionsFocus(false);
      inputRef.current?.focus();
    }
  };

  const onChangeFocus = () => {
    setOptionsFocus(true);
  };

  const handleChangeValueSearchTerm = (value: string) => {
    if (!valueSearchMode) return;
    setValueSearchTerm(value);
  };

  const handleRemoveValue = () => {
    setValueSearchTerm("");
    props.onRemoveValue();
  };

  const getValue = () => {
    if (row.valueProperty) {
      return isEng ? row.valueProperty.name : row.valueProperty.name_de ?? "";
    }
    return row.value ?? "";
  };

  return (
    <div className="relative w-full min-w-[100px]">
      {hideValueInput() ? null : (
        <DropdownBrowser<PropertyBrowserTreeNode>
          displayKey={isEng ? "name" : "name_de"}
          name="value"
          placeholder={t("rulesEditor.selectProperty")}
          items={props.items}
          optionsClassName="!max-w-fit min-w-[100px]"
          containerClassName="relative"
          singleSelect
          fixedPos
          selectInputClassName="w-full"
          labelWithSearch
          buttonClassName={clsx("w-full", {
            "pointer-events-none": row.valueProperty,
          })}
          checkedItems={row.valueProperty ? [row.valueProperty] : []}
          disabled={isTypeNumber()}
          searchTerm={valueSearchTerm}
          isOpen={(state) => {
            setValueSearchMode(state);
            if (!state && !row.value && !row.valueProperty) {
              setValueSearchTerm("");
            }
          }}
          searchTermAsOption={!row.valueProperty}
          isFocused={
            valueSearchMode || document.activeElement === inputRef.current
          }
          optionsFocus={optionsFocus}
          revertFocus={changePropertyFocus}
          titleOrIcon={
            <PropertyInput
              value={valueSearchTerm || getValue()}
              placeholder={t("rulesEditor.selectPropertyOrValue")}
              onRemoveClicked={handleRemoveValue}
              onChangeSearch={handleChangeValueSearchTerm}
              isObject={row.valueProperty && !isCurrentSourceTemplate}
              isProduct={isCurrentSourceTemplate}
              hasError={!row.value && !row.valueProperty && row.hasError}
              type={isTypeNumber() ? "number" : "text"}
              searchMode={valueSearchMode}
              onChangeValue={handleChangeValue}
              changeFocus={onChangeFocus}
              inputRef={inputRef}
            />
          }
          searchInput
          handleSelect={(value) => handleSelectPropertyValue(value[0])}
        />
      )}
    </div>
  );
}

export default observer(ValuePropertyField);
